import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Typography, Button, Divider, styled } from '@mui/material';
import { Row, Column } from 'further-ui/components/Layout';
import FullPageDrawer from 'components/new/FullPageDrawer';
import CloseButton from 'components/new/FullPageDrawer/CloseButton';
import AdminUserRoleSelector from 'components/AdminUserRoleSelector';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import UserInformationForm from '../UserInformationForm';
import FormField from 'further-ui/components/FormField';
import { AddUserFormData } from '../../types';
import { adminUser as rules } from 'further-ui/validations';

const CenteredColumn = styled(Column)({
  width: '400px',
});

type Props = {
  open: boolean;
  onClose: () => void;
  onUserCreated: (userId: string) => void;
};

const AddUserDrawer: React.FC<Props> = ({ open, onClose, onUserCreated }) => {
  const form = useForm<AddUserFormData>({
    resolver: zodResolver(rules.createUserInputForCurrentFirm),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      auth0RoleId: '',
      profilePic: undefined,
    },
  });

  const createUser = api.adminUser.createUserInCurrentFirm.useMutation(
    useHandleTrpcMutation<{ adminUserId: string }>()({
      successMessage:
        'User created successfully. An invitation email has been sent.',
      onSuccess: (data) => {
        form.reset();
        onUserCreated(data.adminUserId);
      },
    }),
  );

  const onSubmit = async (data: AddUserFormData) => {
    createUser.mutate(data);
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const drawerTitle = (
    <Row fullWidth centered spacing="sm" justify="space-between">
      <Row spacing="sm">
        <Typography variant="h4">Add user</Typography>
      </Row>
      <CloseButton onClose={handleClose} />
    </Row>
  );

  return (
    <FullPageDrawer
      open={open}
      onClose={handleClose}
      title={drawerTitle}
      size="md"
      innerScroll
      actions={
        <Row justify="space-between" spacing="sm">
          <Button variant="outlined" size="large" onClick={handleClose}>
            Back
          </Button>
        </Row>
      }
    >
      <CenteredColumn spacing="lg" justify="center">
        <UserInformationForm control={form.control} mode="add" />
        <Divider />
        <FormField label="Role">
          <Column spacing="md">
            <Controller
              name="auth0RoleId"
              control={form.control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <AdminUserRoleSelector
                  value={value}
                  fullWidth
                  onChange={onChange}
                  error={!!error}
                  helperText={error?.message}
                  placeholder="Select role"
                />
              )}
            />
            <Typography variant="body2" color="textSecondary">
              You will be able to restrict access to specific vehicles after
              adding the user.
            </Typography>
          </Column>
        </FormField>
        <Button
          variant="contained"
          size="large"
          onClick={form.handleSubmit(onSubmit)}
          disabled={createUser.isPending}
        >
          Add user
        </Button>
      </CenteredColumn>
    </FullPageDrawer>
  );
};

export default AddUserDrawer;
